import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Warning } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useGetStudentsQuery } from "../../services/students.service";
import { useAppTheme } from "../../context/ThemeContext";

function PassportExpiryWidget() {
  const { data: students } = useGetStudentsQuery("Students");
  const navigate = useNavigate();
  const { isNewTheme } = useAppTheme();

  const now = Date.now();
  const alerts = (students ?? [])
    .map((s) => {
      const expiry = s.passport?.expiryDate ? new Date(s.passport.expiryDate) : null;
      const days = expiry ? Math.ceil((expiry.getTime() - now) / 86400000) : null;
      return { ...s, daysToExpiry: days };
    })
    .filter((s) => s.daysToExpiry !== null && s.daysToExpiry <= 90)
    .sort((a, b) => (a.daysToExpiry ?? 999) - (b.daysToExpiry ?? 999));

  return (
    <Card
      sx={
        isNewTheme
          ? {
              width: "100%",
              height: "100%",
              borderRadius: "8px",
              border: "1px solid #E2E8F0",
              boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
              display: "flex",
              flexDirection: "column",
              transition: "box-shadow 0.15s ease",
              "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.07)" },
            }
          : {
              width: "100%",
              height: "100%",
              borderRadius: "10px",
              boxShadow: "0px 0px 55px 5px rgba(0, 0, 0, 0.05)",
              display: "flex",
              flexDirection: "column",
            }
      }
    >
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isNewTheme && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: "#FEF3C7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Warning sx={{ fontSize: 18, color: "#D97706" }} />
            </Box>
          )}
          <Typography variant="h6" fontWeight={isNewTheme ? 700 : 600} color={isNewTheme ? "text.primary" : "black"}>
            Passport Alerts
          </Typography>
        </Box>
        <Chip
          label={alerts.length}
          size="small"
          color={alerts.length > 0 ? "warning" : "default"}
          sx={{ fontWeight: 700, borderRadius: "4px", minWidth: 28 }}
        />
      </Box>

      <Divider sx={isNewTheme ? { borderColor: "#E2E8F0" } : {}} />

      <CardContent sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
        {alerts.length === 0 ? (
          <Typography
            variant="body2"
            color="text.disabled"
            sx={{ textAlign: "center", mt: 3, fontStyle: "italic" }}
          >
            No passports expiring within 90 days
          </Typography>
        ) : (
          <Stack
            direction="column"
            divider={<Divider sx={isNewTheme ? { borderColor: "#F1F5F9" } : {}} />}
          >
            {alerts.slice(0, 7).map((s) => {
              const expired = (s.daysToExpiry ?? 1) <= 0;
              const critical = !expired && (s.daysToExpiry ?? 91) <= 30;
              return (
                <Tooltip
                  key={s._id}
                  title={`Click to view ${s.fullName}'s profile`}
                  placement="left"
                  arrow
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    p={1}
                    onClick={() => navigate(`/students/${s._id}`)}
                    sx={
                      isNewTheme
                        ? {
                            borderRadius: "8px",
                            cursor: "pointer",
                            transition: "background 0.15s ease",
                            "&:hover": { bgcolor: "#FFFBEB" },
                          }
                        : { cursor: "pointer" }
                    }
                  >
                    <Typography
                      variant="body2"
                      fontWeight={isNewTheme ? 600 : 400}
                      sx={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {s.fullName}
                    </Typography>
                    <Chip
                      label={expired ? "Expired" : `${s.daysToExpiry}d`}
                      size="small"
                      color={expired || critical ? "error" : "warning"}
                      sx={{
                        fontWeight: 700,
                        borderRadius: "4px",
                        fontSize: "0.68rem",
                        height: 20,
                        flexShrink: 0,
                      }}
                    />
                  </Stack>
                </Tooltip>
              );
            })}
            {alerts.length > 7 && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ textAlign: "center", pt: 1 }}
              >
                +{alerts.length - 7} more
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default PassportExpiryWidget;
